import React, { useState, useEffect, useCallback } from "react";
import { Navigation } from "../components/website/navigation";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import axios from "axios";
import { API_ENDPOINTS } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { useAuth } from "../components/router/authContext";
import { LoadingSpinner } from "../components/general/LoadingSpinner";

// Initialize Stripe with environment-specific keys
const stripePromise = loadStripe(
  process.env.NODE_ENV === 'production'
    ? "pk_live_51RBGXnRsYZhZbIEZIaitaVcVGhrKQB6UdLwyHK6d1sXCy7xO9ARIpCs9NMVfawqTIjq1cu2L2AIgbyeSZNdUXTqK00WObG7dw3"
    : "pk_test_51RBGXyRvSZGBetSdltTBNF3ES2Mvhip7x8gQ7snK6xUSHzs1vj6njHx2dAmD4srCpcp40z9f9W2ZaDrwgAntiLcl00enUNq5Ia"
);

// Plan information
const plans = [
  {
    id: "basic",
    name: "Basic Plan",
    price: "Free",
    stripe_price_id: "price_1RCeX0IoPZSqfl8FtUb9rPow", // Add your actual Stripe price ID here
    features: [
      "Add up to 10 routes",
      "No solar and battery storage considerations",
      "Up to 2 depots",
      "One daily run",
      "Basic user support"
    ]
  },
  {
    id: "premium",
    name: "Premium Plan",
    price: 49.99,
    stripe_price_id: process.env.NODE_ENV === 'production' ? "price_1RfsQdRsYZhZbIEZAwtKnObv" : "price_1RfuBHRvSZGBetSdQjcsSfFZ",
    features: [
      "Add up to 50 routes",
      "Solar and battery storage considerations",
      "Up to 10 depots",
      "100 runs per month",
      "Standard user support",
    ]
  },
  {
    id: "enterprise",
    name: "Enterprise Plan",
    price: 99.99,
    stripe_price_id: process.env.NODE_ENV === 'production' ? "price_1RfsP0RsYZhZbIEZw8vNgj4i" : "price_1RfuAERvSZGBetSdS5Bf8LmH",
    features: [
      "Add up to 100 routes",
      "Solar and battery storage considerations",
      "Up to 20 depots",
      "Unlimited runs",
      "Professional direct user support"
    ]
  }
];

function Subscription() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [showEmailVerificationNotice, setShowEmailVerificationNotice] = useState(false);
  const [showChangePlanConfirmation, setShowChangePlanConfirmation] = useState(false);
  const navigate = useNavigate();

  // Get user data from AuthContext
  const { currentUser, isAuthenticated, refreshUserData, isLoading } = useAuth();

  // Animation hooks - must be called before any conditional returns
  const [titleRef, isTitleVisible] = useScrollAnimation({ threshold: 0.5, triggerOnce: true });
  const [plansRef, arePlansVisible] = useScrollAnimation({ threshold: 0.2, triggerOnce: true });

  // Get user data from AuthContext
  const userPlan = currentUser?.subscription_type || 'none';
  const isUserEmailVerified = currentUser?.email_verified || false;
  console.log(currentUser);
  const subscriptionStatus = currentUser?.subscription_status || 'none';

  // All useEffect and useCallback hooks must be before conditional returns
  // Only redirect if auth loading is complete and user is not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login?redirect=true');
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Email Verification Notice
  useEffect(() => {
    if (!isUserEmailVerified && isAuthenticated) {
      setShowEmailVerificationNotice(true);
    }
  }, [isUserEmailVerified, isAuthenticated]);

  const fetchClientSecret = useCallback(async (plan) => {
      // Updating user subscription
      if ((userPlan === 'premium' || userPlan === 'enterprise') && currentUser?.stripe_customer_id && subscriptionStatus === 'active') {
        console.log("Updating subscription");
        try {
          const response = await axios.post(API_ENDPOINTS.updateSubscription, {
            price_id: plan.stripe_price_id,
            subscription_type: plan.id
          }, {
            headers: {
              'Authorization': `Token ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            }
          });
          console.log("Received response:", response.data);
          refreshUserData();
          navigate('/confirmation?update=true');
        } catch (error) {
          console.log("Error:", error);
          setError("Failed to update subscription. Please try again.");
        }
      }
      // Reactivating user subscription
      else if ((userPlan === 'premium' || userPlan === 'enterprise') && currentUser?.stripe_customer_id && subscriptionStatus === 'cancelled') {
        console.log("Reactivating subscription");
        try {
          const response = await axios.post(API_ENDPOINTS.reactivateSubscription, {
            price_id: plan.stripe_price_id,
            subscription_type: plan.id
          }, {
            headers: {
              'Authorization': `Token ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            }
          });
          console.log("Received response:", response.data);
          refreshUserData();
          navigate('/confirmation?update=true');
        } catch (error) {
          console.log("Error:", error);
          setError("Failed to reactivate subscription. Please try again.");
        }
      }
      // Creating new user subscription
      else {
        try {
          const response = await axios.post(API_ENDPOINTS.subscribe, {
            price_id: plan.stripe_price_id,
            subscription_type: plan.id
          }, {
            headers: {
              'Authorization': `Token ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            }
          });

          console.log("Received response:", response.data);

          // Check for client_secret (snake_case) or clientSecret (camelCase)
          const clientSecretValue = response.data.clientSecret || response.data.client_secret;

          if (!clientSecretValue) {
            console.error("No client secret in response:", response.data);
            throw new Error("Server response missing client secret");
          }

          setClientSecret(clientSecretValue);
          refreshUserData();
          return clientSecretValue;
        } catch (err) {
          console.error("Error details:", {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status
          });

          let errorMessage = "Failed to create checkout session";
          if (err.response) {
            errorMessage = err.response.data?.error ||
              `Server error: ${err.response.status}`;
          } else if (err.request) {
            errorMessage = "No response from server. Please check your connection.";
          }

          setError(errorMessage);
          setShowCheckout(false);
          throw new Error(errorMessage);
        }
      }
  }, [userPlan, currentUser, subscriptionStatus, refreshUserData, navigate]);

  // Show loading while auth is being checked
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <LoadingSpinner message="Loading..." animationType="custom-easing-spin" />
      </div>
    );
  }

  const handleSubscribe = () => {
    if (!selectedPlan) return;
    setShowCheckout(true);
  };

  const handleBackToPlans = () => {
    setShowCheckout(false);
    setError(null);
    setClientSecret(null);  // Reset client secret when going back
  };

  // COMMENT THIS OUT WHEN THE SUBSCRIPTION IS READY TO BE LIVE
  const handleSelectedPlan = async (plan) => {
    setSelectedPlan(plan);
    setLoadingCheckout(true);

    // Check authentication first
    if (!isAuthenticated || !currentUser) {
      setError("Please login to continue");
      return;
    }

    if (plan.id === "basic") {
      try {
        // Use currentUser data directly from AuthContext
        const response = await axios.post(
          API_ENDPOINTS.freeSubscribe,
          {}, // Empty object for request body (or add data if needed)
          {
            headers: {
              'Authorization': `Token ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log(response);
        refreshUserData();
        navigate('/confirmation');
      } catch (error) {
        console.log("Error:", error);
        setError("Failed to subscribe to free plan. Please try again.");
        if (error.response) {
          console.log("Response data:", error.response.data);
          console.log("Response status:", error.response.status);
        }
      }
    }
    else if (plan.id === "premium") {
      try {
        // Wait for the client secret to be fetched, then show checkout
        await fetchClientSecret(plan);
        setShowCheckout(true);
      } catch (error) {
        console.log("Error:", error);
        setError("Failed to initialize checkout. Please try again.");
        if (error.response) {
          console.log("Response data:", error.response.data);
          console.log("Response status:", error.response.status);
        }
      }
    } else if (plan.id === "enterprise") {
      try {
        await fetchClientSecret(plan);
        setShowCheckout(true);
      } catch (error) {
        console.log("Error:", error);
        setError("Failed to initialize checkout. Please try again.");
        if (error.response) {
          console.log("Response data:", error.response.data);
          console.log("Response status:", error.response.status);
        }
      }
    } else {
      navigate('/construction');
    }
    setLoadingCheckout(false);
  };

  const options = clientSecret ?
    { clientSecret } :
    {
      fetchClientSecret: () => {
        if (selectedPlan) {
          return fetchClientSecret(selectedPlan);
        }
        return Promise.reject(new Error('No plan selected'));
      }
    };

  const changePlanConfirmationWindow = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999] font-nunito animate-fade-in">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          {
            userPlan === selectedPlan.id && subscriptionStatus === 'cancelled' ?
              <h2 className="text-2xl font-bold text-gray-800">Are you sure you want to reactivate your plan?</h2>
              :
              <h2 className="text-2xl font-bold text-gray-800">Are you sure you want to change your plan?</h2>
          }
          <div className="mt-6 flex gap-4">
            <button className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700" onClick={() => {
              handleSelectedPlan(selectedPlan);
              setShowChangePlanConfirmation(false);
            }}>Yes I'm sure</button>
            <button className="px-6 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300" onClick={() => setShowChangePlanConfirmation(false)}>Never mind</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Navigation />

      {/* Error Overlay */}
      {error && (
        <div className="fixed top-24 right-5 z-50 bg-red-100 border border-red-300 text-red-800 px-5 py-4 rounded-lg shadow-lg flex items-center max-w-md animate-fade-in">
          <div className="flex justify-between items-center w-full">
            <span className="flex-grow mr-4 font-nunito">{error}</span>
            <button
              onClick={() => setError('')}
              className="bg-transparent border-none text-red-800 text-xl font-bold cursor-pointer px-1 leading-none hover:text-red-900"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loadingCheckout && (
        <LoadingSpinner
          message="Initializing secure checkout..."
          animationType="custom-easing-spin"
        />
      )}

      {showChangePlanConfirmation && changePlanConfirmationWindow()}

      <div className="max-w-7xl mx-auto px-5 py-10">
        {!showCheckout && <h1
          ref={titleRef}
          className={`text-center text-3xl font-bold text-gray-800 mb-10 font-nunito transition-all duration-1000 ease-out transform ${isTitleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
          Choose Your Subscription Plan
        </h1>}

        {/* Authentication Notice */}
        {!isAuthenticated && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-center">
            <h3 className="text-lg font-semibold text-blue-800 mb-2 font-nunito">
              Sign in to subscribe
            </h3>
            <p className="text-blue-600 font-nunito mb-4">
              You need to be signed in to choose a subscription plan.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors font-nunito"
            >
              Sign In
            </button>
          </div>
        )}

        {/* Email Verification Notice */}
        {showEmailVerificationNotice && (
          <div className="fixed top-24 right-5 z-50 bg-yellow-100 border border-yellow-300 text-yellow-800 px-5 py-4 rounded-lg shadow-lg flex items-center max-w-md animate-fade-in">
            <div className="flex justify-between items-center w-full">
              <span className="flex-grow mr-4 font-nunito">
                Verify your email to subscribe
              </span>
              <button
                onClick={() => setShowEmailVerificationNotice(false)}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {!showCheckout ? (
          // Plan selection view
          <div>
            <div className="font-nunito">
              <h1 className="font-semibold text-xl my-4">AmpTrans Fleet Planner</h1>
            </div>
            <div
              ref={plansRef}
              className={`w-full transition-all duration-1000 ease-out transform ${arePlansVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
              <div className="flex flex-wrap justify-center gap-8 mb-4">
                {plans.map(plan => (
                  <div
                    key={plan.id}
                    className={`flex-1 min-w-[280px] max-w-[350px] bg-white rounded-lg shadow-lg pt-12 p-8 transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col justify-between hover:-translate-y-1 hover:shadow-xl ${selectedPlan?.id === plan.id ? 'border-2 border-blue-600 -translate-y-1 shadow-xl' : ''
                      } ${userPlan === plan.id && subscriptionStatus === 'active' ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    {/* Current Plan Badge */}
                    {userPlan === plan.id && subscriptionStatus === 'active' && (
                      <div className="absolute top-4 right-4 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                        Current Plan
                      </div>
                    )}

                    <div className="text-center mb-5 pb-5 border-b border-gray-100">
                      <h2 className="text-xl font-semibold text-gray-800 mb-2 font-nunito">
                        {plan.name}
                      </h2>
                      <div className="text-3xl font-bold text-blue-600 font-nunito">
                        {plan.price === "Free" ? "Free" : `$${plan.price}`}
                        {plan.price !== "Free" && (
                          <span className="text-base font-normal text-gray-500">/month</span>
                        )}
                      </div>
                    </div>

                    <div className="mb-8 flex-grow">
                      <ul className="pl-5 space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="text-gray-600 font-nunito">
                            ✓ {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button
                      className={`w-full py-3 rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 mt-auto font-nunito ${(userPlan === plan.id && subscriptionStatus === 'active')
                        ? 'bg-green-600 text-white cursor-default'
                        : selectedPlan?.id === plan.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setSelectedPlan(plan);
                        if (userPlan === plan.id && subscriptionStatus === 'active') return; // Don't allow selecting current plan
                        if (userPlan === plan.id && subscriptionStatus === 'cancelled') {
                          setShowChangePlanConfirmation(true);
                          return;
                        }
                        else if (userPlan !== plan.id && subscriptionStatus === 'active') {
                          setShowChangePlanConfirmation(true);
                          return;
                        }
                        else {
                          await handleSelectedPlan(plan);
                        }
                      }}
                      disabled={userPlan === plan.id && subscriptionStatus === 'active'}
                    >
                      {userPlan === plan.id && subscriptionStatus === 'active'
                        ? 'Current Plan'
                        : userPlan === plan.id && subscriptionStatus === 'cancelled'
                          ? 'Reactivate Plan'
                          : userPlan !== plan.id && subscriptionStatus === 'active'
                            ? 'Change Plan'
                            : 'Select Plan'}
                    </button>
                  </div>
                ))}

                {/* Custom Plan Card */}
                <div className="flex-1 min-w-[280px] max-w-[350px] bg-white rounded-lg shadow-lg p-8 transition-all duration-300 relative overflow-hidden flex flex-col justify-between hover:-translate-y-1 hover:shadow-xl">
                  <div className="flex flex-col justify-center leading-relaxed">
                    <h2 className="text-xl font-semibold text-center text-gray-800 mb-4 font-nunito">
                      Looking for a tailored solution?
                    </h2>
                    <p className="text-gray-600 font-nunito">
                      If you don't see a plan that perfectly fits your needs, please don't hesitate to contact us.
                      Send us an email at{' '}
                      <span className="text-blue-600 font-bold">info@amptrans.net</span>{' '}
                      and we can provide consulting and create a custom plan that matches your specific requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Checkout view
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden animate-fade-in">
            <div className="px-8 py-5 bg-gray-50 border-b border-gray-200">
              <div className="flex justify-between items-center py-2">
                <span className="font-semibold text-gray-800 font-nunito">{selectedPlan.name}</span>
                <span className="text-blue-600 font-bold font-nunito">${selectedPlan.price}/month</span>
              </div>
            </div>

            {error && (
              <div className="mx-5 my-5 p-4 bg-red-50 text-red-600 rounded-lg text-sm text-center font-nunito">
                {error}
              </div>
            )}

            <div className="min-h-[500px] p-5">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={options}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>

            <div className="flex justify-center">
              <button
                onClick={handleBackToPlans}
                className="block px-10 mx-5 mb-5 py-3 bg-transparent text-blue-600 border border-blue-600 rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 hover:bg-blue-50 font-nunito"
              >
                ← Back to Plans
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Subscription;